import React, { useState } from 'react'
import { ReactComponent as DrawerToggleCaret } from '@assets/images/Icons/Drawer_toggle_caret.svg'
import { Tooltip } from '../Tooltip'
import type { DrawerItem, DrawerProps } from './Drawer.types'
import './Drawer.css'

const Drawer: React.FC<DrawerProps> = ({
  items,
  footerItems = [],
  activeId,
  onSelect,
  expanded,
  defaultExpanded = false,
  onExpandedChange,
  darkMode = false,
  ariaLabel = 'Main navigation',
  className = '',
  previewState,
}) => {
  const isPreview = previewState != null
  const isControlled = expanded !== undefined
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded)
  const isExpanded = isControlled ? expanded : internalExpanded

  const toggleExpanded = (): void => {
    if (isPreview) {
      return
    }

    const next = !isExpanded
    if (!isControlled) {
      setInternalExpanded(next)
    }

    onExpandedChange?.(next)
  }

  const handleSelect = (id: string): void => {
    if (isPreview) {
      return
    }

    onSelect?.(id)
  }

  const rootClassNames = [
    'ds-drawer',
    isExpanded ? 'ds-drawer--expanded' : 'ds-drawer--collapsed',
    darkMode ? 'ds-drawer--dark' : '',
    isPreview ? 'ds-drawer--preview' : '',
    previewState ? `ds-drawer--preview-${previewState}` : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const renderItem = (item: DrawerItem): React.ReactNode => {
    const isActive = item.id === activeId

    const itemButton = (
      <button
        type="button"
        className="ds-drawer__item"
        aria-current={isActive ? 'page' : undefined}
        aria-label={!isExpanded ? item.label : undefined}
        disabled={isPreview}
        onClick={() => handleSelect(item.id)}
      >
        <span className="ds-drawer__item-icon" aria-hidden="true">
          {item.icon}
        </span>
        <span className="ds-drawer__item-label">{item.label}</span>
      </button>
    )

    return (
      <li key={item.id} className="ds-drawer__list-item">
        {!isExpanded && !isPreview ? (
          <Tooltip content={item.label} placement="right" portal>
            {itemButton}
          </Tooltip>
        ) : (
          itemButton
        )}
      </li>
    )
  }

  return (
    <nav className={rootClassNames} aria-label={ariaLabel}>
      <div className="ds-drawer__inner">
        <div className="ds-drawer__scroll">
          <button
            type="button"
            className="ds-drawer__toggle"
            aria-label={
              isExpanded ? 'Collapse navigation' : 'Expand navigation'
            }
            aria-expanded={isExpanded}
            disabled={isPreview}
            onClick={toggleExpanded}
          >
            <DrawerToggleCaret
              className="ds-drawer__toggle-icon"
              aria-hidden="true"
            />
          </button>

          <ul className="ds-drawer__list">{items.map(renderItem)}</ul>
        </div>

        {footerItems.length > 0 ? (
          <ul className="ds-drawer__list ds-drawer__footer">
            {footerItems.map(renderItem)}
          </ul>
        ) : null}
      </div>
    </nav>
  )
}

export default Drawer
